import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useParams } from "react-router-dom";
import CustomerHeader from "@View/pages/AdminPO/customer/customerDetails/common/CustomerHeader";
import { Tooltip } from "@material-ui/core";
import DetailsIcon from "@View/components/svgComponents/DetailsIcon";
import { canAddAbility, canDeleteGroup, canEditEverything } from "@Security/stateHelpers";
import EditIcon from "@View/components/svgComponents/EditIcon";
import DeleteIcon from "@View/components/svgComponents/DeleteIcon";
import { useTranslation } from "react-i18next";
import { useTooltipStyle } from "@View/style/components/tooltipStyles";
import ButtonAddNew from "@View/components/buttons/ButtonAddNew";
import DataTable from "@View/components/table/DataTable";
import { getGroupData } from "@State/actions/actionsGet/actionGetCustomer";
import { formatDateTime } from "@Localization/utils";
import NewGroupConsolidationModal from "@View/pages/AdminPO/customer/customerDetails/modals/NewGroupConsolidationModal";
import EditGroupConsolidationModal from "@View/pages/AdminPO/customer/customerDetails/modals/EditGroupConsolidationModal";
import DownloadIcon from "@View/components/svgComponents/DownloadIcon";
import UploadIcon from "@mui/icons-material/CloudUpload";
import { downloadFile } from "@Dao/api";
import ViewIcon from "@View/components/svgComponents/ViewIcon";
import PreviewGroupConsolidationModal from "@View/pages/AdminPO/customer/customerDetails/modals/PreviewGroupConsolidationModal";
import { useLocale } from "@Utils/composables";
import { DeleteModal } from "@View/components/DeleteModal";
import getAPI from "@API/index";

const CustomerGroupPage = ({ customer, groups, groupData, loadGroupData }) => {
  const { customerId, groupId } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const tooltipStyle = useTooltipStyle();

  const [openNew, setOpenNew] = useState(false);
  const handleOpenNew = () => {
    setOpenNew(true);
  };
  const handleCloseNew = () => {
    setOpenNew(false);
  };

  const reloadData = useCallback(() => {
    loadGroupData(customerId, groupId);
  }, [loadGroupData, customerId, groupId]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  const [activeConsolidation, setActiveConsolidation] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const handleOpenEdit = () => {
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const [openFinData, setOpenFinData] = useState(false);
  const handleOpenFinData = () => {
    setOpenFinData(true);
  };
  const handleCloseFinData = () => {
    setOpenFinData(false);
  };

  // Delete confirmation state
  const [deleteConsolidation, setDeleteConsolidation] = useState(null);

  const handleDeleteConfirm = async () => {
    if (!deleteConsolidation) return;

    try {
      await getAPI().request({
        method: "delete",
        url: `/api/customer/${customerId}/consolidation/${deleteConsolidation.id}`,
        successText: "snackbar.success",
      });
      reloadData();
    } catch (error) {
      console.error("Failed to delete consolidation:", error);
    } finally {
      setDeleteConsolidation(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConsolidation(null);
  };

  const handleUploadReport = (consolidationId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        await getAPI().request({
          method: "post",
          url: `/api/customer/${customerId}/consolidation/${consolidationId}/upload-report`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          successText: "snackbar.success",
        });
        reloadData();
      } catch (error) {
        console.error("Failed to upload report:", error);
      }
    };
    input.click();
  };

  const tableHeader = [
    {
      field: "name",
      headerName: t("inputs.Label"),
      flex: 2,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "capacity",
      headerName: t("ClientCapacityTitle"),
      flex: 3,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        const clients = cell.row.clients || [];
        const visibleCount = 2;
        const visibleClients = clients.slice(0, visibleCount);
        const remainingCount = clients.length - visibleCount;

        const formatDate = (d) => new Date(d).toLocaleDateString("ru-RU");

        const getClientNote = (client) => {
          // Компания не участвует в расчёте
          if (client.isNotInCalculation) {
            return " (Не участвует в расчёте)";
          }
          // Компания добавлена позже И удалена раньше
          if (client.isAddedLater && client.isRemovedEarly) {
            return ` (с ${formatDate(client.includedFromDate)} по ${formatDate(client.includedUntilDate)})`;
          }
          // Только добавлена позже
          if (client.isAddedLater && client.includedFromDate) {
            return ` (с ${formatDate(client.includedFromDate)})`;
          }
          // Только удалена раньше
          if (client.isRemovedEarly && client.includedUntilDate) {
            return ` (до ${formatDate(client.includedUntilDate)})`;
          }
          return "";
        };

        const fullList = clients.map(c => {
          const note = getClientNote(c);
          return `${c.name} (ИНН: ${c.externalId || "—"})${note}`;
        }).join("\n");

        return (
          <Tooltip
            title={<div style={{ whiteSpace: "pre-line" }}>{fullList}</div>}
            classes={tooltipStyle}
            placement="bottom-start"
          >
            <div style={{ lineHeight: "18px", fontWeight: 400, cursor: "pointer" }}>
              {visibleClients.map((client) => {
                const note = getClientNote(client);
                return (
                  <div key={client.id}>
                    {client.name} (ИНН: {client.externalId || "—"})
                    {note && <span style={{ color: "#ff9800", fontSize: "0.85em" }}>{note}</span>}
                  </div>
                );
              })}
              {remainingCount > 0 && (
                <div style={{ color: "#2960EE", fontWeight: 500 }}>
                  +{remainingCount} ещё...
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "startDate",
      headerName: t("inputs.Start Date"),
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        return (
          <div style={{ lineHeight: "16px", fontWeight: 400 }}>
            {cell.row.startDate}
          </div>
        );
      },
    },
    {
      field: "endDate",
      headerName: t("inputs.End Date"),
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        return (
          <div style={{ lineHeight: "16px", fontWeight: 400 }}>
            {cell.row.endDate}
          </div>
        );
      },
    },
    {
      field: "updatedAt",
      headerName: t("inputs.Updated At"),
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        return (
          <div style={{ lineHeight: "16px", fontWeight: 400 }}>
            {formatDateTime(cell.row.updatedAt)}
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: t("inputs.Status"),
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        return (
          <div style={{ lineHeight: "16px", fontWeight: 400 }}>
            {cell.row.status}
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: " ",
      flex: 1.5,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        return (
          <div className="tableIcons">
            <Tooltip title={"Периоды консолидации"} classes={tooltipStyle}>
              <div
                onClick={() => {
                  setActiveConsolidation(cell.row);
                  handleOpenFinData();
                }}
                className={tooltipStyle.element}
                style={cell.row.hasPendingPeriods ? {
                  color: "#F5A623",
                  animation: "pendingPulse 1.5s ease-in-out infinite",
                } : {}}
              >
                <DetailsIcon />
              </div>
            </Tooltip>
            <Tooltip title={"Загрузить финансовую отчетность по шаблону(.xlsx)"} classes={tooltipStyle}>
              <div
                onClick={() => handleUploadReport(cell.row.id)}
                className={tooltipStyle.element}
              >
                <UploadIcon />
              </div>
            </Tooltip>
            <Tooltip title={t("buttons.Get data")} classes={tooltipStyle}>
              <div
                onClick={() => {
                  downloadFile(
                    `/api/customer/${customerId}/consolidation/${cell.row.id}/file`
                  );
                }}
                className={[tooltipStyle.element]}
              >
                <DownloadIcon />
              </div>
            </Tooltip>
            {canEditEverything() && canAddAbility() && (
              <Tooltip title={t("buttons.Edit")} classes={tooltipStyle}>
                <div
                  onClick={() => {
                    setActiveConsolidation(cell.row);
                    handleOpenEdit();
                  }}
                  className={tooltipStyle.element}
                >
                  <EditIcon />
                </div>
              </Tooltip>
            )}
            <Tooltip title={t("buttons.Details")} classes={tooltipStyle}>
              <Link
                to={`/consolidation/${customerId}/request/${cell.row.id}`}
                className={tooltipStyle.element}
              >
                <ViewIcon />
              </Link>
            </Tooltip>
            {canDeleteGroup() && (
              <Tooltip title={t("buttons.Delete")} classes={tooltipStyle}>
                <div
                  onClick={() => setDeleteConsolidation(cell.row)}
                  className={tooltipStyle.element}
                >
                  <DeleteIcon />
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="customerDetails__page">
      <CustomerHeader customerId={customerId} />
      {groupData && (
        <DataTable
          rows={groupData.consolidations}
          columns={tableHeader}
          toolbarButtons={<ButtonAddNew onClick={handleOpenNew} />}
          rowHeight={70}
        />
      )}
      {groupData && (
        <NewGroupConsolidationModal
          open={openNew}
          customerId={customerId}
          group={groupData}
          handleOpen={handleOpenNew}
          handleClose={handleCloseNew}
          reloadData={reloadData}
        />
      )}
      {groupData && activeConsolidation && (
        <EditGroupConsolidationModal
          open={openEdit}
          customerId={customerId}
          group={groupData}
          consolidation={activeConsolidation}
          handleOpen={handleOpenEdit}
          handleClose={handleCloseEdit}
          reloadData={reloadData}
        />
      )}
      {groupData && activeConsolidation && (
        <PreviewGroupConsolidationModal
          open={openFinData}
          customerId={customerId}
          group={groupData}
          consolidation={activeConsolidation}
          handleOpen={handleOpenFinData}
          handleClose={handleCloseFinData}
        />
      )}
      <DeleteModal
        handleDelete={handleDeleteConfirm}
        openModal={!!deleteConsolidation}
        handleCloseModal={handleDeleteCancel}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    customer: state.customer.customer,
    groups: state.customer.groups,
    groupData: state.customer.group,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    loadGroupData: (customerId, groupId) =>
      dispatch(getGroupData(customerId, groupId)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(CustomerGroupPage);
